import {
  Box,
  CircularProgress,
  InputBase,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  SupervisorAccountOutlined as GroupAdminsIcon,
  WhereToVote as ActiveIcon,
  BlockOutlined as InactiveIcon,
} from "@mui/icons-material";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import moment from "moment";
import {getCompanyGroupAdminsPaged, getCompanyGroupAdminStats} from "../services/system/CompanyGroupAdminService";
import AddCompanyGroupAdminDialog from "../componenets/administrators/AddCompanyGroupAdminDialog";
import CompanyChips from "../componenets/administrators/CompanyChips";
import {StatusChip, FilterDropdown, Pagination} from "../componenets/ui";
import {t} from "../componenets/dashboard/coach/coachTokens";
import {KPI} from "../componenets/dashboard/coach/KpiRow";
import {useAuth} from "../contexts/AuthContext";

/* ── Constants & helpers ─────────────────────────────────── */
const PAGE_SIZE = 20;
const STATUS_OPTS = [
  {value: "ALL", label: "הכול"},
  {value: "true", label: "פעיל", color: t.green},
  {value: "false", label: "לא פעיל", color: t.red},
];

/* ── Table cell presets (match Companies) ─── */
const td = {padding: "10px 12px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle"};
const th = {padding: "10px 12px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
            background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em"};

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.trim().slice(0, 2).toUpperCase();
};

/* ═══════════════════════════════════════════════════════════
   Company Group Administrators page
   ═══════════════════════════════════════════════════════════ */
const CompanyGroupAdmins = () => {
  const navigate = useNavigate();
  // Managing group admins is a super-admin action; a group admin (should it reach this page by URL)
  // gets no "new group admin" CTA — it has no creation capabilities.
  const {isCompanyGroupAdmin} = useAuth();
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [newAdminOpen, setNewAdminOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [reload, setReload] = useState(false);

  const [f, setF] = useState({
    admins: [],
    total: 0,
    totalPages: 0,
    keyword: query.get("keyword") || "",
    enabled: query.get("enabled") != null ? query.get("enabled") === "true" : null,
    page: +(query.get("page") || 0),
    loading: true,
  });

  // Debounced search
  const [searchInput, setSearchInput] = useState(f.keyword);
  const debounceRef = useRef(null);
  const handleSearch = useCallback((val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => set({keyword: val}), 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // URL sync
  const sync = (ns) => {
    const p = new URLSearchParams();
    if (ns.keyword) p.set("keyword", ns.keyword);
    if (ns.enabled != null) p.set("enabled", ns.enabled);
    if (ns.page > 0) p.set("page", ns.page);
    navigate(p.toString() ? `${location.pathname}?${p}` : location.pathname, {replace: true});
  };

  const set = (patch) => { const ns = {...f, page: 0, ...patch}; setF(ns); sync(ns); };

  // Load stats (also refresh after a create)
  useEffect(() => { getCompanyGroupAdminStats().then(r => setStats(r.data)).catch(() => {}); }, [reload]);

  // Fetch administrators
  useEffect(() => {
    setF(p => ({...p, loading: true}));
    getCompanyGroupAdminsPaged(f.page, PAGE_SIZE, f.keyword || null, f.enabled)
        .then(r => setF(p => ({
          ...p,
          admins: r.data.content,
          total: r.data.totalElements,
          totalPages: r.data.totalPages,
          loading: false,
        })))
        .catch(() => setF(p => ({...p, loading: false})));
  }, [f.page, f.keyword, f.enabled, reload]);

  // Status filter value <-> dropdown string ("ALL" | "true" | "false")
  const statusValue = f.enabled == null ? "ALL" : String(f.enabled);
  const onStatusChange = (v) => set({enabled: v === "ALL" ? null : v === "true"});

  const pages = Math.ceil(f.total / PAGE_SIZE);

  /* ── Render ──────────────────────────────────────────── */
  return (
      <Box sx={{display: "flex", flexDirection: "column", gap: "18px"}}>
        {/* Dialog */}
        <AddCompanyGroupAdminDialog open={newAdminOpen} close={() => setNewAdminOpen(false)} setReload={setReload}/>

        {/* ─── Title row ─── */}
        <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0}}>
          <Box>
            <Typography sx={{fontSize: {xs: "22px", md: "26px"}, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px"}}>
              מנהלי קבוצות חברות
            </Typography>
            <Typography sx={{fontSize: "13px", color: t.t2, mt: 0.25}}>ניהול מנהלי קבוצות חברות</Typography>
          </Box>
          {!isCompanyGroupAdmin() && (
          <Box component="button" onClick={() => setNewAdminOpen(true)} sx={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            background: t.green, color: "#fff",
            height: 34, px: "15px", borderRadius: t.rs,
            fontSize: "13px", fontWeight: 600, letterSpacing: "-0.1px",
            border: "none", cursor: "pointer", fontFamily: "inherit",
            transition: "all .14s",
            "&:hover": {background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
          }}>
            <AddIcon sx={{fontSize: 16}}/>
            מנהל קבוצת חברות חדש
          </Box>
          )}
        </Box>

        {/* ─── KPI grid ─── */}
        <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "repeat(3, 1fr)"}, gap: "14px", flexShrink: 0}}>
          <KPI
              icon={<GroupAdminsIcon sx={{fontSize: 22}}/>} iconBg={t.greenLight} iconColor={t.green}
              value={stats?.total?.toLocaleString() ?? "—"} valueColor={t.text}
              label="סה״כ מנהלי קבוצות חברות"
              onClick={() => set({enabled: null})}
          />
          <KPI
              icon={<ActiveIcon sx={{fontSize: 22}}/>} iconBg={t.greenLight} iconColor={t.green}
              value={stats?.active?.toLocaleString() ?? "—"} valueColor={t.green}
              label="מנהלים פעילים"
              onClick={() => set({enabled: true})}
          />
          <KPI
              icon={<InactiveIcon sx={{fontSize: 22}}/>} iconBg={t.redLight} iconColor={t.red}
              value={stats?.inactive?.toLocaleString() ?? "—"} valueColor={t.red}
              label="מנהלים לא פעילים"
              onClick={() => set({enabled: false})}
          />
        </Box>

        {/* ─── Table card ─── */}
        <Box sx={{
          background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r,
          boxShadow: t.sh, overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}>

          {/* Filter bar inside card */}
          <Box sx={{
            display: "flex", alignItems: "center", gap: "7px",
            padding: "10px 14px", borderBottom: `1px solid ${t.border}`,
            background: t.bg, flexWrap: "wrap", flexShrink: 0, direction: "rtl",
          }}>
            {/* Count pill */}
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: "4px",
              fontSize: "12px", fontWeight: 700, color: t.greenText,
              background: t.greenLight, border: "1px solid rgba(1,187,167,.2)",
              padding: "4px 12px", borderRadius: "20px", whiteSpace: "nowrap",
            }}>
              <GroupAdminsIcon sx={{fontSize: 14}}/>
              {f.total} מנהלים
            </Box>

            {/* Search */}
            <Box sx={{position: "relative"}}>
              <SearchIcon sx={{position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: t.t3, pointerEvents: "none"}}/>
              <InputBase
                  value={searchInput}
                  onChange={e => handleSearch(e.target.value)}
                  placeholder="חיפוש לפי שם או אימייל..."
                  sx={{
                    width: 240,
                    padding: "6px 10px 6px 30px",
                    border: `1.5px solid ${t.border}`,
                    borderRadius: t.rs,
                    background: t.white,
                    color: t.text,
                    fontSize: "12px",
                    "& input": {padding: 0},
                    "& input::placeholder": {color: t.t3, opacity: 1},
                    "&:focus-within": {borderColor: t.green, boxShadow: "0 0 0 3px rgba(1,187,167,.08)"},
                  }}
              />
            </Box>

            <FilterDropdown label="סטטוס" value={statusValue} onChange={onStatusChange} options={STATUS_OPTS} minWidth={120}/>
          </Box>

          {/* Table area */}
          {f.loading && !f.admins.length ? (
              <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 540}}>
                <CircularProgress size={32} sx={{color: t.green}}/>
              </Box>
          ) : !f.admins.length ? (
              <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240, flexDirection: "column", gap: 1, padding: "48px"}}>
                <GroupAdminsIcon sx={{fontSize: 48, color: t.border}}/>
                <Typography sx={{color: t.t3, fontSize: "15px", fontWeight: 500}}>אין מנהלי קבוצות חברות</Typography>
                <Typography sx={{color: t.t3, fontSize: "13px"}}>הוסף מנהל קבוצת חברות חדש כדי להתחיל</Typography>
              </Box>
          ) : (
              <>
                <TableContainer sx={{overflowY: "auto", maxHeight: 600, direction: "rtl"}}>
                  <Table stickyHeader sx={{tableLayout: "fixed"}}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{...th, textAlign: "right", width: "28%"}}>מנהל</TableCell>
                        <TableCell sx={{...th, textAlign: "right"}}>חברות</TableCell>
                        <TableCell sx={{...th, textAlign: "center", width: "14%", display: {xs: "none", md: "table-cell"}}}>תאריך יצירה</TableCell>
                        <TableCell sx={{...th, textAlign: "center", width: "12%"}}>סטטוס</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {f.admins.map(admin => (
                          <TableRow key={admin.id}
                                    onClick={() => navigate(`/app/administrators/${admin.id}`)}
                                    sx={{cursor: "pointer", "&:hover td": {background: t.greenLight}, transition: "background .12s ease"}}>
                            <TableCell sx={{...td, textAlign: "right"}}>
                              <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                <Box sx={{
                                  width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                                  background: t.greenLight, color: t.greenText,
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  fontSize: "11px", fontWeight: 700,
                                }}>
                                  {getInitials(admin.name)}
                                </Box>
                                <Box sx={{minWidth: 0}}>
                                  <Typography sx={{fontSize: "12.5px", fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                                    {admin.name}
                                  </Typography>
                                  <Typography sx={{fontSize: "11px", color: t.t2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", direction: "ltr", textAlign: "right"}}>
                                    {admin.email}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell sx={{...td, textAlign: "right"}}>
                              <CompanyChips companies={admin.companies}/>
                            </TableCell>
                            <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                              <Typography sx={{fontSize: "12.5px", fontWeight: 500, color: t.t2}}>
                                {moment.unix(admin.createdDate).format("DD/MM/YY")}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{...td, textAlign: "center"}}>
                              {admin.enabled
                                  ? <StatusChip label="פעיל" variant="success" size="small"/>
                                  : <StatusChip label="לא פעיל" variant="error" size="small"/>}
                            </TableCell>
                          </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {pages > 1 && (
                    <Pagination currentPage={f.page} totalPages={pages} totalItems={f.total} itemsPerPage={PAGE_SIZE}
                                onPageChange={p => { const ns = {...f, page: p}; setF(ns); sync(ns); }}/>
                )}
              </>
          )}
        </Box>
      </Box>
  );
};

export default CompanyGroupAdmins;
